/**
 * Spec §14.2 page 8: nightly report, coverage %, findings, confidence, review states.
 * Never claims a clean conversation when no audio was reviewed (spec §14.3).
 */
import { useState } from "react";

import { useReportDetail, useReports, useReviewFinding } from "../api/hooks.js";
import { CoverageNote, StateBadge } from "../components/StateBadge.js";
import { Empty, PageState } from "../components/PageState.js";

export function AiNannyPage({ deviceId }: { deviceId: string }) {
  const reports = useReports(deviceId);
  const review = useReviewFinding();
  const [reportId, setReportId] = useState<string | null>(null);
  const detail = useReportDetail(reportId ?? reports.data?.[0]?.id ?? null);

  if (!deviceId) return <Empty label="No device selected." />;
  const rows = reports.data ?? [];

  return (
    <PageState query={{ isPending: reports.isPending, error: reports.error }}>
      {rows.length === 0 ? (
        <p className="notice">
          No nightly AI Nanny report yet — the batch runs after the local ASR pipeline processes consented audio.
        </p>
      ) : (
        <div className="stack">
          {rows.map((r) => (
            <button key={r.id} className="row" onClick={() => setReportId(r.id)} aria-label={`Open ${r.report_date_local}`}>
              <strong>{r.report_date_local}</strong>
              <StateBadge state={r.status} />
              <span className="muted">coverage {JSON.stringify(r.coverage)}</span>
            </button>
          ))}
        </div>
      )}
      {detail.data ? (
        <section className="card stack">
          <h3>Report {detail.data.report_date_local}</h3>
          <CoverageNote coverage={detail.data.coverage} />
          <pre className="mono">{detail.data.summary}</pre>
          <p className="muted">
            {detail.data.finding_ids.length} linked finding(s). Findings are only shown when the nightly pipeline
            produced them; absence of findings is not evidence of an absence of risk.
          </p>
          {detail.data.finding_ids.map((fid) => (
            <div key={fid} className="row">
              <span className="mono">{fid.slice(0, 8)}…</span>
              <button disabled={review.isPending} onClick={() => review.mutate({ findingId: fid, reviewState: "dismissed", note: "false positive" })}>
                Dismiss
              </button>
              <button disabled={review.isPending} onClick={() => review.mutate({ findingId: fid, reviewState: "resolved", note: "handled with child" })}>
                Resolve
              </button>
            </div>
          ))}
          {review.data ? <p className="notice">Finding {review.data.review_state}.</p> : null}
        </section>
      ) : (
        <p className="muted">{detail.isPending ? "Loading report…" : "Select a report."}</p>
      )}
    </PageState>
  );
}
